// src/services/inquiryService.js
// 78차 65차: 답변을 단발성 reply 필드 → replies 서브컬렉션(대댓글)으로 교체 —
// 문의사항이 한 번에 해결되지 않을 수 있어(재문의/재답변) 여러 번 주고받을 수 있게 함.
// 72차 신규: 문의사항 — 두 가지 창구
//   1) targetType:'store' — 특정 매장에 대한 문의 (예약/테마 화면에서 작성, storeId 필수)
//   2) targetType:'app'   — 앱 자체에 대한 문의 (마이탭에서 작성, storeId 없음 — 총괄 관리자만 응대)
// 쿼리에 orderBy를 안 쓰고 클라이언트에서 정렬 — where 단일 필드만 쓰면 복합 인덱스가
// 필요 없어서 firestore.indexes.json을 안 건드려도 된다(reviews/getReviewsByStoreId 때와 달리).
// 75차: senderRole 필드 추가 — targetType:'app'은 지금까지 일반 회원/매장관리자 구분 없이
// 전부 한 목록에 섞였음. 정산 이의제기 등 매장관리자가 총괄관리자에게 보내는 문의를
// "문의사항(매장)"으로 구분해서 보여주기 위해 작성자 역할을 스냅샷으로 남긴다.
// 75차: 데드코드 정리 — getInquiryById 삭제(호출부 없음, 딥링크는 리스트 화면으로만 이동)
import {
  collection, doc, addDoc, updateDoc, getDocs, query, where, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';

const COL = 'inquiries';
const byNewest = (a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);

// ─── 문의 작성 ────────────────────────────────────────────────
// context: { type: 'reservation'|'theme', reservationId?, productId?, productName? } | null
// imageUrl: 테마수정/추가/삭제 문의(WriteInquiryScreen.js)에서 첨부하는 테마 이미지 URL
export const createInquiry = async ({ uid, targetType, storeId = null, context = null, title, content, senderRole = 'user', category = null, imageUrl = null }) => {
  if (!uid || !targetType || !title?.trim() || !content?.trim()) {
    throw new Error('필수 항목이 비어있습니다.');
  }
  if (targetType === 'store' && !storeId) {
    throw new Error('매장 문의는 storeId가 필요합니다.');
  }
  await addDoc(collection(db, COL), {
    uid,
    targetType,
    storeId,
    context,
    senderRole, // 75차: 'user' | 'store' — targetType:'app' 문의를 총괄관리자 화면에서 구분 표시
    category, // 78차 35차: 요금/앱 오류/계정/제안/기타(app), 예약/결제/매장 정보/불편사항/기타(store)
    title: title.trim(),
    content: content.trim(),
    imageUrl: imageUrl || null,
    status: 'pending',
    createdAt: serverTimestamp(),
  });
};

// ─── 내 문의 목록 (customer) ──────────────────────────────────
export const getMyInquiries = async (uid) => {
  if (!uid) return [];
  const snap = await getDocs(query(collection(db, COL), where('uid', '==', uid)));
  return snap.docs.map(d => ({ id: d.id, ...d.data() })).sort(byNewest);
};

// ─── 매장 문의 목록 (store admin) — 여러 매장 관리자면 storeIds 배열로 합산 조회 ──
// 75차: targetType==='store'만 — app 문의(정산 이의제기 등)도 storeId를 갖게 되면서
// (WriteInquiryScreen.js) storeId만으로 걸러내면 매장관리자 자신이 총괄관리자에게 보낸
// 문의까지 "고객 문의" 목록에 섞여 보이는 문제가 있었음.
export const getStoreInquiries = async (storeIds) => {
  const ids = Array.isArray(storeIds) ? storeIds : [storeIds];
  if (!ids.length) return [];
  const lists = await Promise.all(ids.filter(Boolean).map(async (sid) => {
    const snap = await getDocs(query(collection(db, COL), where('storeId', '==', sid)));
    return snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(d => d.targetType === 'store');
  }));
  return lists.flat().sort(byNewest);
};

// ─── 앱 전체 문의 목록 (super admin) ───────────────────────────
export const getAppInquiries = async () => {
  const snap = await getDocs(query(collection(db, COL), where('targetType', '==', 'app')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() })).sort(byNewest);
};

// 78차 65차: 단발성 reply 필드 → replies 서브컬렉션(대댓글, 여러 번 주고받기)으로 교체.
// 문의사항이 한 번에 해결되지 않을 수 있어(추가 확인 필요 등), 관리자 답변 후 문의자가
// 다시 메시지를 남기고 관리자가 또 답할 수 있어야 한다는 요청 반영.
// ─── 답변/후속 메시지 스레드 조회 ──────────────────────────────
export const getInquiryReplies = async (inquiryId) => {
  const snap = await getDocs(query(collection(db, COL, inquiryId, 'replies'), orderBy('createdAt', 'asc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ─── 답변/후속 메시지 추가 ──────────────────────────────────────
// role: 'admin'(관리자 답변) | 'user'(문의자 후속 메시지)
// 관리자가 답하면 status:'answered', 문의자가 후속 메시지를 남기면 다시 status:'pending'
// (관리자가 아직 확인 안 한 상태로 재오픈 — 목록의 "대기중" 필터/배지가 다시 잡히게 함)
export const addInquiryReply = async (inquiryId, { uid, role, text }) => {
  if (!inquiryId || !uid || !role || !text?.trim()) {
    throw new Error('필수 파라미터 누락');
  }
  await addDoc(collection(db, COL, inquiryId, 'replies'), {
    uid, role, text: text.trim(), createdAt: serverTimestamp(),
  });
  await updateDoc(doc(db, COL, inquiryId), {
    status: role === 'admin' ? 'answered' : 'pending',
  });
};
